import { __awaiter, __generator, __read, __spreadArray } from "tslib";
import { useCallback, useContext, useEffect, useRef } from 'react';
import { ChannelIO } from '../ChannelIO';
import { ReactChannelIOContext } from '../context';
/**
 * Using ChannelIO apis through React hook.
 *
 * @link https://developers.channel.io/docs/web-channel-io
 */
export var useChannelIOApi = function () {
    var isBooted = useRef(false);
    var context = useContext(ReactChannelIOContext);
    if (!context) {
        throw new Error('Oops, looks like you forgot Provider for ChannelIO hooks.');
    }
    useEffect(function () {
        if (context.isBooted) {
            isBooted.current = context.isBooted;
        }
    }, [context.isBooted]);
    /**
     * ### `showMessenger`
     *
     * Show plugin messenger
     *
     * @link https://developers.channel.io/docs/web-channel-io#showmessenger
     */
    var showMessenger = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['showMessenger'], __read(args)));
    }, []);
    /**
     * ### `show`
     *
     * Show plugin messenger
     *
     * @link https://developers.channel.io/docs/web-channel-io#show
     *
     * @deprecated
     * `show` will be supported until 2022. After that time it will be deprecated.
     * Recommend to use `showMessenger` instead.
     */
    var show = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['show'], __read(args)));
    }, []);
    /**
     * ### `hideMessenger`
     *
     * Hide plugin messenger
     *
     * @link https://developers.channel.io/docs/web-channel-io#hidemessenger
     */
    var hideMessenger = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['hideMessenger'], __read(args)));
    }, []);
    /**
     * ### `hide`
     *
     * Hide plugin messenger
     *
     * @link https://developers.channel.io/docs/web-channel-io#hide
     *
     * @deprecated
     * `hide` will be supported until 2022. After that time it will be deprecated.
     * Recommend to use `hideMessenger` instead.
     */
    var hide = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['hide'], __read(args)));
    }, []);
    /**
     * ### `lounge`
     *
     * Go to the lounge view.
     *
     * @link https://developers.channel.io/docs/web-channel-io#lounge
     *
     * @deprecated
     * `lounge` will be supported until 2022. After that time it will be deprecated.
     * `lounge` API won't work in all mobile environments.
     */
    var lounge = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['lounge'], __read(args)));
    }, []);
    /**
     * ### `openChat`
     *
     * Open a chat with the given chat id and message.
     * If the given chat id exists, appropriate chat will be opened.
     * If not, lounge will be opened. In this case, the message will be ignored.
     * If chat id is empty and message is given, new chat will be opened and the given message will be put in the input box.
     * In this case, the support bot will not run.
     * if chat id and message is both empty, new chat will be opened.
     *
     * @link https://developers.channel.io/docs/web-channel-io#openchat
     *
     * @param chatId The id of the chat
     * @param message The message which will be put in the input box on messenger when new chat is opened
     */
    var openChat = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['openChat'], __read(args)));
    }, []);
    /**
     * ### `track`
     *
     * Track an event
     *
     * @link https://developers.channel.io/docs/web-channel-io#track
     *
     * @param eventName name of event, its length should be less than 30
     * @param eventProperty an object contains key/value information
     */
    var track = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['track'], __read(args)));
    }, []);
    /**
     * ### `updateUser`
     *
     * Update user information.
     *
     * @link https://developers.channel.io/docs/web-channel-io#updateuser
     */
    var updateUser = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (!isBooted.current) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        ChannelIO('updateUser', args[0], function (err, user) {
                            if (err) {
                                reject(err);
                                return;
                            }
                            resolve(user);
                        });
                    })];
            });
        });
    }, []);
    /**
     * ### `addTags`
     *
     * Add tags.
     *
     * @link https://developers.channel.io/docs/web-channel-io#addtags
     *
     * @param array Tags to be added.
     * Duplicate values are maintained.
     * Combined tag list cannot exceed 10.
     * Null or empty list is not allowed.
     * Always lower case.
     */
    var addTags = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (!isBooted.current) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        ChannelIO('addTags', args[0], function (err, user) {
                            if (err) {
                                reject(err);
                                return;
                            }
                            resolve(user);
                        });
                    })];
            });
        });
    }, []);
    /**
     * ### `removeTags`
     *
     * Remove tags.
     *
     * @link https://developers.channel.io/docs/web-channel-io#removetags
     *
     * @param array Tags to be erased.
     * If there is no match tag value, it is ignored.
     * Null or empty list is not allowed.
     */
    var removeTags = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (!isBooted.current) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        ChannelIO('removeTags', args[0], function (err, user) {
                            if (err) {
                                reject(err);
                                return;
                            }
                            resolve(user);
                        });
                    })];
            });
        });
    }, []);
    /**
     * ### `setPage`
     *
     * Set page to be used instead of canonical url .
     *
     * `setPage` with null or undefined is different from resetPage. (that will send page data with null)
     *
     * @link https://developers.channel.io/docs/web-channel-io#setpage
     *
     * @param page Page data to replace default page value
     */
    var setPage = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['setPage'], __read(args)));
    }, []);
    /**
     * ### `resetPage`
     *
     * Reset page data customized by developer.
     *
     * If you call resetPage, page data will fill with canonical url .
     *
     * @link https://developers.channel.io/docs/web-channel-io#resetpage
     */
    var resetPage = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['resetPage'], __read(args)));
    }, []);
    /**
     * ### `showChannelButton`
     *
     * Show channel button.
     *
     * @link https://developers.channel.io/docs/web-channel-io#showchannelbutton
     */
    var showChannelButton = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['showChannelButton'], __read(args)));
    }, []);
    /**
     * ### `hideChannelButton`
     *
     * Hide channel button.
     *
     * @link https://developers.channel.io/docs/web-channel-io#hidechannelbutton
     */
    var hideChannelButton = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!isBooted.current) {
            return;
        }
        ChannelIO.apply(void 0, __spreadArray(['hideChannelButton'], __read(args)));
    }, []);
    return {
        boot: context.boot,
        shutdown: context.shutdown,
        showMessenger: showMessenger,
        show: show,
        hideMessenger: hideMessenger,
        hide: hide,
        lounge: lounge,
        openChat: openChat,
        track: track,
        updateUser: updateUser,
        addTags: addTags,
        removeTags: removeTags,
        setPage: setPage,
        resetPage: resetPage,
        showChannelButton: showChannelButton,
        hideChannelButton: hideChannelButton,
    };
};
